import React from "react";
import {
	App as AntdApp,
	Avatar,
	Button, Card,
	Empty,
	Form,
	Input,
	Modal,
	Popconfirm,
	Space,
	Spin,
	Table,
	Tabs,
	Tooltip,
	Typography,
	Upload
} from "antd";
import {
	DeleteOutlined,
	EditOutlined,
	PictureOutlined,
	PlusOutlined
} from "@ant-design/icons";

import api from "@/api/axios.js";

const {Text} = Typography;

const ENDPOINTS = {
	categories: "/api/categories",
	classes: "/api/classes",
	badges: "/api/badges",
	filters: "/api/filters",
	mechanics: "/api/mechanics",
	funds: "/api/funds",
	focus: "/api/taxonomy-values/focus",
	stile: "/api/taxonomy-values/stile",
	variante: "/api/taxonomy-values/variante",
	fondo: "/api/taxonomy-values/fondo",
	banner: "/api/taxonomy-values/banner",
	banner_collegato: "/api/taxonomy-values/banner_collegato",
};

const MEDIA_BUCKETS = {
	categories: "categories",
	classes: "classes",
	badges: "badges",
	filters: "filters",
	mechanics: "mechanics",
	funds: "funds"
};

const MAX_UPLOAD_SIZE = 5 * 1024 * 1024;
const ALLOWED_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/webp"];
const DUAL_IMAGE_ENTITIES = new Set(["classes", "badges", "mechanics"]);

// Scheda delle sei tassonomie storiche → campo del catalogo da cui dipende.
const LEGACY_ENTITY_FIELD_KEYS = {
	categories: "categoria",
	classes: "classe",
	badges: "bollino",
	filters: "filtro",
	mechanics: "meccanica",
	funds: "fondi"
};

const LABELS = {
	categories: {
		singular: "categoria",
		plural: "Categorie",
		addLabel: "Aggiungi categoria",
		gender: "f"
	},
	classes: {
		singular: "classe",
		plural: "Classi",
		addLabel: "Aggiungi classe",
		gender: "f"
	},
	badges: {
		singular: "bollino",
		plural: "Bollini",
		addLabel: "Aggiungi bollino",
		gender: "m"
	},
	filters: {
		singular: "filtro",
		plural: "Filtri",
		addLabel: "Aggiungi filtro",
		gender: "m"
	},
	mechanics: {
		singular: "meccanica",
		plural: "Meccaniche",
		addLabel: "Aggiungi meccanica",
		gender: "f"
	},
	funds: {
		singular: "fondo",
		plural: "Fondi",
		addLabel: "Aggiungi fondo",
		gender: "m"
	},
	focus: {
		singular: "focus",
		plural: "Focus",
		addLabel: "Aggiungi focus",
		gender: "m"
	},
	stile: {
		singular: "stile",
		plural: "Stili",
		addLabel: "Aggiungi stile",
		gender: "m"
	},
	variante: {
		singular: "variante",
		plural: "Varianti",
		addLabel: "Aggiungi variante",
		gender: "f"
	},
	fondo: {
		singular: "fondo (colore)",
		plural: "Fondi (colore)",
		addLabel: "Aggiungi fondo (colore)",
		gender: "m"
	},
	banner: {
		singular: "banner",
		plural: "Banner",
		addLabel: "Aggiungi banner",
		gender: "m"
	},
	banner_collegato: {
		singular: "banner collegato",
		plural: "Banner collegati",
		addLabel: "Aggiungi banner collegato",
		gender: "m"
	},
};

function selectedAdjective(count, gender)
{
	if (gender === "f")
		return count === 1 ? "selezionata" : "selezionate";
	return count === 1 ? "selezionato" : "selezionati";
}

function deletedParticiple(count, gender)
{
	if (gender === "f")
		return count === 1 ? "eliminata" : "eliminate";
	return count === 1 ? "eliminato" : "eliminati";
}

function notDeletedPhrase(count, gender)
{
	if (gender === "f")
		return count === 1 ? "non è stata eliminata" : "non sono state eliminate";
	return count === 1 ? "non è stato eliminato" : "non sono stati eliminati";
}

import icon from "@/assets/imgs/logo-icon.png";

function normalizeImagePath(path)
{
	if (!path)
		return icon;

	const trimmed = String(path).trim();
	if (!trimmed) return "";
	if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:"))
		return trimmed;

	try
	{
		const base = api?.defaults?.baseURL || window.location.origin;
		return new URL(trimmed, base).href;
	}
	catch (err)
	{
		console.warn("Impossibile normalizzare imagePath", err);
		return trimmed;
	}
}

function formatDate(value)
{
	if (!value) return "-";
	try
	{
		const date = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(date.getTime())) return "-";
		return new Intl.DateTimeFormat("it-IT", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit"
		}).format(date);
	}
	catch
	{
		return "-";
	}
}

const INITIAL_DATA = {categories: [], classes: [], badges: [], filters: [], mechanics: [], funds: [], focus: [], stile: [], variante: [], fondo: [], banner: [], banner_collegato: []};
const INITIAL_LOADING = {categories: false, classes: false, badges: false, filters: false, mechanics: false, funds: false, focus: false, stile: false, variante: false, fondo: false, banner: false, banner_collegato: false};
const INITIAL_SELECTION = {categories: [], classes: [], badges: [], filters: [], mechanics: [], funds: [], focus: [], stile: [], variante: [], fondo: [], banner: [], banner_collegato: []};
const INITIAL_BULK_DELETING = {categories: false, classes: false, badges: false, filters: false, mechanics: false, funds: false, focus: false, stile: false, variante: false, fondo: false, banner: false, banner_collegato: false};

export default function OrgTaxonomyPanel({cooperativaId, cooperativaName = "", registryFieldKeys = null, registryLoading = false})
{
	const {message, notification} = AntdApp.useApp();
	const [data, setData] = React.useState(INITIAL_DATA);
	const [loading, setLoading] = React.useState(INITIAL_LOADING);
	const [modalForm] = Form.useForm();
	const [modalState, setModalState] = React.useState({open: false, mode: "create", entity: "categories", record: null});
	const [actionLoading, setActionLoading] = React.useState({});
	const [selectedIds, setSelectedIds] = React.useState(INITIAL_SELECTION);
	const [bulkDeleting, setBulkDeleting] = React.useState(INITIAL_BULK_DELETING);
	const latestIdRef = React.useRef(cooperativaId);
	const watchedImageFilePositive = Form.useWatch("imageFilePositive", modalForm);
	const watchedImageFileNegative = Form.useWatch("imageFileNegative", modalForm);
	const watchedImageFileSingle = Form.useWatch("imageFile", modalForm);
	const hasPositiveImage = Array.isArray(watchedImageFilePositive) && watchedImageFilePositive.length > 0;
	const hasNegativeImage = Array.isArray(watchedImageFileNegative) && watchedImageFileNegative.length > 0;
	const hasSingleImage = Array.isArray(watchedImageFileSingle) && watchedImageFileSingle.length > 0;

	React.useEffect(() =>
	{
		latestIdRef.current = cooperativaId;
	}, [cooperativaId]);

	const openModal = React.useCallback((entity, mode, record = null) =>
	{
		setModalState({open: true, entity, mode, record});
	}, []);

	React.useEffect(() =>
	{
		if (!modalState.open)
			return;

		const record = modalState.record;
		const isDualImage = DUAL_IMAGE_ENTITIES.has(modalState.entity);
		const hasImageField = modalState.entity === "categories"
			|| modalState.entity === "badges"
			|| modalState.entity === "classes"
			|| modalState.entity === "filters"
			|| modalState.entity === "mechanics"
			|| modalState.entity === "funds";
		if (record)
		{
			const nextValues = {name: record.name ?? ""};
			if (hasImageField)
			{
				if (isDualImage)
				{
					const positivePath = record.imagePathPositive || record.imagePath || null;
					const negativePath = record.imagePathNegative || null;

					nextValues.imagePathPositive = positivePath;
					nextValues.imagePathNegative = negativePath;
					nextValues.imageFilePositive = positivePath
						? [{
							uid: `${record.id || record._id || record.name}-positive-${positivePath}`,
							name: String(positivePath).split("/").pop() || record.name || "immagine-positiva",
							status: "done",
							url: normalizeImagePath(positivePath),
							response: {imagePath: positivePath}
						}]
						: [];
					nextValues.imageFileNegative = negativePath
						? [{
							uid: `${record.id || record._id || record.name}-negative-${negativePath}`,
							name: String(negativePath).split("/").pop() || record.name || "immagine-negativa",
							status: "done",
							url: normalizeImagePath(negativePath),
							response: {imagePath: negativePath}
						}]
						: [];

					nextValues.imagePath = positivePath ?? null;
				}
				else
				{
					const imagePath = record.imagePath ?? null;
					nextValues.imageFile = imagePath
						? [{
							uid: record.id || record._id || record.name,
							name: String(imagePath).split("/").pop() || record.name || "immagine",
							status: "done",
							url: normalizeImagePath(imagePath),
							response: {imagePath}
						}]
						: [];
					nextValues.imagePath = imagePath;
				}
			}
			modalForm.setFieldsValue(nextValues);
		}
		else
		{
			const nextValues = {name: ""};
			if (hasImageField)
			{
				if (isDualImage)
				{
					nextValues.imageFilePositive = [];
					nextValues.imageFileNegative = [];
					nextValues.imagePathPositive = null;
					nextValues.imagePathNegative = null;
					nextValues.imagePath = null;
				}
				else
				{
					nextValues.imageFile = [];
					nextValues.imagePath = null;
				}
			}
			modalForm.setFieldsValue(nextValues);
		}
	}, [modalState.open, modalState.record, modalState.entity, modalForm]);

	const resetState = React.useCallback(() =>
	{
		setData(INITIAL_DATA);
		setLoading(INITIAL_LOADING);
		setSelectedIds(INITIAL_SELECTION);
	}, []);

	const fetchTaxonomy = React.useCallback(async (entity, {silent} = {silent: false}) =>
	{
		if (!cooperativaId)
			return;

		setLoading(prev => ({...prev, [entity]: true}));
		const currentId = cooperativaId;

		try
		{
			const response = await api.get(ENDPOINTS[entity], {
				params: {cooperativaId}
			});
			const payload = response?.data;
			const items = Array.isArray(payload)
				? payload
				: (payload?.items || payload?.data || []);

			if (latestIdRef.current !== currentId)
				return;

			setData(prev => ({...prev, [entity]: items}));

			const validIds = new Set(items.map(item => item.id || item._id));
			setSelectedIds(prev => ({...prev, [entity]: (prev[entity] || []).filter(id => validIds.has(id))}));
		}
		catch (err)
		{
			if (!silent)
			{
				const description = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Errore di caricamento";
				notification.error({
					message: `Impossibile caricare ${LABELS[entity].plural.toLowerCase()}`,
					description,
					placement: "bottomRight"
				});
			}
		}
		finally
		{
			if (latestIdRef.current === currentId)
				setLoading(prev => ({...prev, [entity]: false}));
		}
	}, [cooperativaId, notification]);

	React.useEffect(() =>
	{
		modalForm.resetFields();
		setModalState(state => ({...state, open: false, record: null}));
		setSelectedIds(INITIAL_SELECTION);

		if (!cooperativaId)
		{
			resetState();
			return;
		}

		const loadAll = async () =>
		{
			await Promise.all([
				fetchTaxonomy("categories", {silent: true}),
				fetchTaxonomy("classes", {silent: true}),
				fetchTaxonomy("badges", {silent: true}),
				fetchTaxonomy("filters", {silent: true}),
				fetchTaxonomy("mechanics", {silent: true}),
				fetchTaxonomy("funds", {silent: true}),
				fetchTaxonomy("focus", {silent: true}),
				fetchTaxonomy("stile", {silent: true}),
				fetchTaxonomy("variante", {silent: true}),
				fetchTaxonomy("fondo", {silent: true}),
				fetchTaxonomy("banner", {silent: true}),
				fetchTaxonomy("banner_collegato", {silent: true}),
			]);
		};

		loadAll();
	}, [cooperativaId, fetchTaxonomy, modalForm, resetState]);

	const closeModal = React.useCallback(() =>
	{
		setModalState(prev => ({...prev, open: false, record: null}));
		modalForm.resetFields();
	}, [modalForm]);

	const uploadTaxonomyImage = React.useCallback(async (kind, {file, onError, onProgress, onSuccess}) =>
	{
		const entity = modalState.entity;
		const bucket = MEDIA_BUCKETS[entity];
		const isDualImage = DUAL_IMAGE_ENTITIES.has(entity);

		if (!bucket)
		{
			const error = new Error("Upload non disponibile per questa entità");
			onError?.(error);
			return;
		}

		try
		{
			const formData = new FormData();
			formData.append("image", file);

			const response = await api.post(`/api/media/${bucket}`, formData, {
				headers: {"Content-Type": "multipart/form-data"},
				onUploadProgress(progressEvent)
				{
					if (!progressEvent.total)
						return;

					const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
					onProgress?.({percent});
				}
			});

			const imagePath = response?.data?.imagePath || null;
			const normalizedUrl = imagePath ? normalizeImagePath(imagePath) : null;
			const uploadFile = {
				uid: file.uid,
				name: file.name,
				status: "done",
				url: normalizedUrl || undefined,
				thumbUrl: normalizedUrl || undefined,
				type: file.type,
				size: file.size,
				response: {imagePath}
			};

			if (!isDualImage)
			{
				modalForm.setFieldsValue({
					imagePath: imagePath || null,
					imageFile: imagePath ? [uploadFile] : []
				});
			}
			else if (kind === "positive")
			{
				modalForm.setFieldsValue({
					imagePathPositive: imagePath || null,
					imageFilePositive: imagePath ? [uploadFile] : [],
					imagePath: imagePath || null
				});
			}
			else if (kind === "negative")
			{
				modalForm.setFieldsValue({
					imagePathNegative: imagePath || null,
					imageFileNegative: imagePath ? [uploadFile] : []
				});
			}
			onSuccess?.({imagePath}, uploadFile);
		}
		catch (err)
		{
			const description = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Upload fallito";
			message.error(description);
			onError?.(err);
		}
	}, [modalState.entity, modalForm, message]);

	const handleSubmit = React.useCallback(async () =>
	{
		try
		{
			const values = await modalForm.validateFields();
			const entity = modalState.entity;
			const hasImageField = entity === "categories"
				|| entity === "badges"
				|| entity === "classes"
				|| entity === "filters"
				|| entity === "mechanics"
				|| entity === "funds";
			const isDualImage = DUAL_IMAGE_ENTITIES.has(entity);
			const payload = {name: values.name.trim()};
			setModalState(prev => ({...prev, submitting: true}));

			if (hasImageField)
			{
				if (isDualImage)
				{
					const positiveList = Array.isArray(values.imageFilePositive) ? values.imageFilePositive : [];
					const negativeList = Array.isArray(values.imageFileNegative) ? values.imageFileNegative : [];
					const hasPendingUpload = [...positiveList, ...negativeList].some(file => file?.status && file.status !== "done");
					if (hasPendingUpload)
						throw new Error("Attendi il completamento dell'upload prima di salvare");

					const positivePath = positiveList[0]?.response?.imagePath || null;
					const negativePath = negativeList[0]?.response?.imagePath || null;

					payload.imagePathPositive = positivePath;
					payload.imagePathNegative = negativePath;
					payload.imagePath = positivePath;
				}
				else
				{
					const fileList = Array.isArray(values.imageFile) ? values.imageFile : [];
					const hasPendingUpload = fileList.some(file => file?.status && file.status !== "done");
					if (hasPendingUpload)
						throw new Error("Attendi il completamento dell'upload prima di salvare");

					const imagePath = fileList[0]?.response?.imagePath || null;
					payload.imagePath = imagePath;
				}
			}

			if (modalState.mode === "create")
			{
				await api.post(ENDPOINTS[entity], {
					cooperativaId,
					...payload
				});
				message.success(`Nuova ${LABELS[entity].singular} creata`);
			}
			else if (modalState.record?.id || modalState.record?._id)
			{
				const id = modalState.record.id || modalState.record._id;
				await api.patch(`${ENDPOINTS[entity]}/${id}`, payload);
				const capitalized = `${LABELS[entity].singular.charAt(0).toUpperCase()}${LABELS[entity].singular.slice(1)}`;
				message.success(`${capitalized} aggiornata`);
			}

			await fetchTaxonomy(entity, {silent: true});
			closeModal();
		}
		catch (err)
		{
			if (err?.response?.status === 409)
			{
				const description = err?.response?.data?.message || err?.response?.data?.error || "Nome già esistente";
				modalForm.setFields([
					{name: "name", errors: [description]}
				]);
				return;
			}

			if (err?.errorFields)
				return; // error già mostrato da AntD

			const entity = modalState.entity;
			const action = modalState.mode === "create" ? "creare" : "aggiornare";
			const description = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Errore imprevisto";
			notification.error({
				message: `Impossibile ${action} la ${LABELS[entity].singular}`,
				description,
				placement: "bottomRight"
			});
		}
		finally
		{
			setModalState(prev => ({...prev, submitting: false}));
		}
	}, [modalForm, modalState, cooperativaId, fetchTaxonomy, closeModal, message, notification]);

	const handleDelete = React.useCallback(async (entity, record) =>
	{
		const id = record.id || record._id;
		if (!id) return;

		setActionLoading(prev => ({...prev, [`${entity}-${id}`]: true}));

		try
		{
			await api.delete(`${ENDPOINTS[entity]}/${id}`);
			message.success(`${LABELS[entity].singular.charAt(0).toUpperCase()}${LABELS[entity].singular.slice(1)} eliminata`);
			await fetchTaxonomy(entity, {silent: true});
		}
		catch (err)
		{
			const description = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Errore di eliminazione";
			notification.error({
				message: `Impossibile eliminare la ${LABELS[entity].singular}`,
				description,
				placement: "bottomRight"
			});
		}
		finally
		{
			setActionLoading(prev => ({...prev, [`${entity}-${id}`]: false}));
		}
	}, [fetchTaxonomy, message, notification]);

	const handleBulkDelete = React.useCallback(async (entity) =>
	{
		const ids = selectedIds[entity] || [];
		if (!ids.length) return;

		setBulkDeleting(prev => ({...prev, [entity]: true}));

		try
		{
			const results = await Promise.allSettled(
				ids.map(id => api.delete(`${ENDPOINTS[entity]}/${id}`))
			);
			const failedCount = results.filter(result => result.status === "rejected").length;
			const successCount = results.length - failedCount;
			const {singular, plural, gender} = LABELS[entity];
			const pluralLower = plural.toLowerCase();

			if (successCount)
				message.success(`${successCount} ${successCount === 1 ? singular : pluralLower} ${deletedParticiple(successCount, gender)}`);

			if (failedCount)
			{
				notification.error({
					message: "Eliminazione parziale",
					description: `${failedCount} ${failedCount === 1 ? singular : pluralLower} ${notDeletedPhrase(failedCount, gender)}`,
					placement: "bottomRight"
				});
			}

			setSelectedIds(prev => ({...prev, [entity]: []}));
			await fetchTaxonomy(entity, {silent: true});
		}
		finally
		{
			setBulkDeleting(prev => ({...prev, [entity]: false}));
		}
	}, [selectedIds, fetchTaxonomy, message, notification]);

	const nameValidator = React.useCallback((_, value) =>
	{
		const entity = modalState.entity;
		const trimmed = typeof value === "string" ? value.trim() : "";
		if (!trimmed)
			return Promise.reject(new Error("Il nome è obbligatorio"));

		const list = data[entity] || [];
		const currentId = modalState.record?.id || modalState.record?._id;
		const exists = list.some(item =>
			(item.id || item._id) !== currentId && item.name?.trim()?.toLowerCase() === trimmed.toLowerCase()
		);

		if (exists)
			return Promise.reject(new Error("Esiste già un elemento con questo nome"));

		return Promise.resolve();
	}, [modalState, data]);

	const renderImageCell = React.useCallback((entity, record) =>
	{
		const label = record.name?.[0]?.toUpperCase() || "?";

		if (DUAL_IMAGE_ENTITIES.has(entity))
		{
			const positiveUrl = normalizeImagePath(record.imagePathPositive || record.imagePath);
			const negativeUrl = normalizeImagePath(record.imagePathNegative);
			return (
				<Space size={6}>
					<Tooltip title="Positiva">
						<Avatar
							shape="square"
							src={positiveUrl || undefined}
							style={{padding: 5}}
							size={42}
							className={`org-taxonomy-thumb${positiveUrl ? "" : " org-taxonomy-thumb--placeholder"}`}
						>
							{positiveUrl ? null : label}
						</Avatar>
					</Tooltip>
					<Tooltip title="Negativa">
						<Avatar
							shape="square"
							src={negativeUrl || undefined}
							style={{padding: 5}}
							size={42}
							className={`org-taxonomy-thumb${negativeUrl ? "" : " org-taxonomy-thumb--placeholder"}`}
						>
							{negativeUrl ? null : label}
						</Avatar>
					</Tooltip>
				</Space>
			);
		}

		const url = normalizeImagePath(record.imagePath);
		return (
			<Avatar
				shape="square"
				src={url || undefined}
				style={{padding: 5}}
				size={50}
				className={`org-taxonomy-thumb${url ? "" : " org-taxonomy-thumb--placeholder"}`}
			>
				{url ? null : label}
			</Avatar>
		);
	}, []);

	const renderActions = React.useCallback((entity) => (value, record) =>
	{
		const key = record.id || record._id;
		const loadingKey = `${entity}-${key}`;
		const list = [
			<Tooltip key="rename" title="Rinomina">
				<Button
					size="small"
					icon={<EditOutlined/>}
					onClick={() => openModal(entity, "edit", record)}
				/>
			</Tooltip>
		];

		if (entity === "categories" || entity === "badges" || entity === "classes" || entity === "filters" || entity === "mechanics" || entity === "funds")
		{
			list.push(
				<Tooltip key="image" title="Cambia immagine">
					<Button
						size="small"
						icon={<PictureOutlined/>}
						onClick={() => openModal(entity, "edit", record)}
					/>
				</Tooltip>
			);
		}

		list.push(
			<Popconfirm
				key="delete"
				title="Confermi l'eliminazione?"
				okText="Elimina"
				cancelText="Annulla"
				placement="topRight"
				onConfirm={() => handleDelete(entity, record)}
			>
				<Button
					size="small"
					danger
					icon={<DeleteOutlined/>}
					loading={!!actionLoading[loadingKey]}
				/>
			</Popconfirm>
		);

		return <Space size={8}>{list}</Space>;
	}, [openModal, handleDelete, actionLoading]);

	const columns = React.useMemo(() =>
	{
		return {
			categories: [
				{
					title: "Miniatura",
					dataIndex: "imagePath",
					key: "imagePath",
					align: "center",
					render: (_, record) => renderImageCell("categories", record),
					width: 1
				},
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("categories"),
					width: 1
				}
			],
			classes: [
				{
					title: "Miniatura",
					dataIndex: "imagePath",
					key: "imagePath",
					align: "center",
					render: (_, record) => renderImageCell("classes", record),
					width: 1
				},
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("classes"),
					width: 1
				}
			],
			filters: [
				{
					title: "Miniatura",
					dataIndex: "imagePath",
					key: "imagePath",
					align: "center",
					render: (_, record) => renderImageCell("filters", record),
					width: 1
				},
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("filters"),
					width: 1
				}
			],
			mechanics: [
				{
					title: "Miniatura",
					dataIndex: "imagePath",
					key: "imagePath",
					align: "center",
					render: (_, record) => renderImageCell("mechanics", record),
					width: 1
				},
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("mechanics"),
					width: 1
				}
			],
			funds: [
				{
					title: "Miniatura",
					dataIndex: "imagePath",
					key: "imagePath",
					align: "center",
					render: (_, record) => renderImageCell("funds", record),
					width: 1
				},
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("funds"),
					width: 1
				}
			],
			badges: [
				{
					title: "Miniatura",
					dataIndex: "imagePath",
					key: "imagePath",
					align: "center",
					render: (_, record) => renderImageCell("badges", record),
					width: 1
				},
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("badges"),
					width: 1
				}
			],
			focus: [
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("focus"),
					width: 1
				}
			],
			stile: [
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("stile"),
					width: 1
				}
			],
			variante: [
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("variante"),
					width: 1
				}
			],
			fondo: [
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("fondo"),
					width: 1
				}
			],
			banner: [
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("banner"),
					width: 1
				}
			],
			banner_collegato: [
				{
					title: "Nome",
					dataIndex: "name",
					key: "name",
					render: (value) => <Text strong>{value}</Text>,
					ellipsis: true
				},
				{
					title: "Aggiornato",
					dataIndex: "updatedAt",
					key: "updatedAt",
					render: (_, record) => formatDate(record.updatedAt || record.createdAt)
				},
				{
					title: "Azioni",
					key: "actions",
					align: "center",
					render: renderActions("banner_collegato"),
					width: 1
				}
			],
		};
	}, [renderActions, renderImageCell]);

	const tabsItems = React.useMemo(() =>
	{
		return ["categories", "classes", "badges", "filters", "mechanics", "funds", "focus", "stile", "variante", "fondo", "banner", "banner_collegato"].filter((entity) =>
		{
			// Le sei tassonomie storiche hanno una scheda fissa: senza questo controllo restava
			// visibile anche dopo aver eliminato il campo dal catalogo (es. "Fondi").
			const fieldKey = LEGACY_ENTITY_FIELD_KEYS[entity];
			if (!fieldKey)
				return true;
			if (registryLoading)
				return false;
			return !registryFieldKeys || registryFieldKeys.has(fieldKey);
		}).map((entity) => ({
			key: entity,
			label: LABELS[entity].plural,
			children: (
				<div className="org-taxonomy-tab">
					<div className="org-taxonomy-toolbar">
						<Button
							type="primary"
							icon={<PlusOutlined/>}
							onClick={() => openModal(entity, "create")}
							disabled={!cooperativaId}
						>
							{LABELS[entity].addLabel}
						</Button>
						<Popconfirm
							title={`Confermi l'eliminazione di ${(selectedIds[entity] || []).length} ${(selectedIds[entity] || []).length === 1 ? LABELS[entity].singular : LABELS[entity].plural.toLowerCase()}?`}
							okText="Elimina"
							cancelText="Annulla"
							placement="bottomLeft"
							disabled={!(selectedIds[entity] || []).length}
							onConfirm={() => handleBulkDelete(entity)}
						>
							<Button
								danger
								icon={<DeleteOutlined/>}
								disabled={!(selectedIds[entity] || []).length}
								loading={!!bulkDeleting[entity]}
							>
								Elimina {selectedAdjective((selectedIds[entity] || []).length, LABELS[entity].gender)}{(selectedIds[entity] || []).length ? ` (${(selectedIds[entity] || []).length})` : ""}
							</Button>
						</Popconfirm>
					</div>
					<div className="org-taxonomy-table">
						{loading[entity]
							? (
								<div className="org-taxonomy-loading">
									<Spin size="large"/>
								</div>
							)
							: (data[entity]?.length
									? (
										<Table
											size="middle"
											rowKey={(record) => record.id || record._id || record.name}
											columns={columns[entity]}
											dataSource={data[entity]}
											rowSelection={{
												selectedRowKeys: selectedIds[entity] || [],
												onChange: (keys) => setSelectedIds(prev => ({...prev, [entity]: keys}))
											}}
											pagination={{position: ["bottomCenter"], pageSize: 5, hideOnSinglePage: true}}
											scroll={{x: true}}
										/>
									)
									: (
										<div className="org-taxonomy-empty">
											<Empty description={`Nessuna ${LABELS[entity].singular} presente`} image={Empty.PRESENTED_IMAGE_SIMPLE}/>
										</div>
									)
							)
						}
					</div>
				</div>
			)
		}));
	}, [columns, cooperativaId, data, loading, openModal, selectedIds, bulkDeleting, handleBulkDelete, registryFieldKeys, registryLoading]);

	return (
		<div className="org-taxonomy-panel">
			{
				cooperativaId
					? (<Tabs className="org-taxonomy-tabs" type="card" items={tabsItems} style={{marginTop: 10}}/>)
					: (<div className="org-taxonomy-placeholder">
						<Empty
							description="Seleziona una cooperativa per gestire le risorse"
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					</div>)
			}

			<Modal
				open={modalState.open}
				title={modalState.mode === "create" ? LABELS[modalState.entity].addLabel : `Modifica ${LABELS[modalState.entity].singular}`}
				okText={modalState.mode === "create" ? "Crea" : "Salva"}
				cancelText="Annulla"
				confirmLoading={!!modalState.submitting}
				onCancel={closeModal}
				onOk={handleSubmit}
				forceRender
			>
				<Form
					form={modalForm}
					layout="vertical"
					preserve={false}
				>
					{(modalState.entity === "categories" || modalState.entity === "badges" || modalState.entity === "classes" || modalState.entity === "filters" || modalState.entity === "mechanics" || modalState.entity === "funds") && (
						<Form.Item name="imagePath" hidden>
							<Input type="hidden"/>
						</Form.Item>
					)}

					<Form.Item
						label="Nome"
						name="name"
						rules={[{validator: nameValidator}]}
					>
						<Input placeholder={`Nome ${LABELS[modalState.entity].singular}`}/>
					</Form.Item>

					{(modalState.entity === "categories" || modalState.entity === "badges" || modalState.entity === "classes" || modalState.entity === "filters" || modalState.entity === "mechanics" || modalState.entity === "funds") && (
						DUAL_IMAGE_ENTITIES.has(modalState.entity) ? (
							<>
								<Form.Item name="imagePathPositive" hidden>
									<Input type="hidden"/>
								</Form.Item>
								<Form.Item name="imagePathNegative" hidden>
									<Input type="hidden"/>
								</Form.Item>

								<div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12}}>
									<Card title="Immagine positiva" style={{textAlign: 'center'}}>
										<Form.Item
											name="imageFilePositive"
											valuePropName="fileList"
											getValueFromEvent={(event) =>
											{
												const fileList = Array.isArray(event) ? event : (event?.fileList || []);
												const list = fileList.slice(-1);
												return list.map(item => ({
													uid: item.uid,
													name: item.name,
													status: item.status,
													url: item.url || (item.response?.imagePath ? normalizeImagePath(item.response.imagePath) : undefined),
													thumbUrl: item.thumbUrl,
													type: item.type,
													size: item.size,
													percent: item.percent,
													response: item.response?.imagePath ? {imagePath: item.response.imagePath} : undefined
												}));
											}}
											extra="Immagine con fondo chiaro"
											style={{display: 'flex', justifyContent: 'center'}}
										>
											<Upload
												accept="image/*"
												listType="picture-card"
												maxCount={1}
												customRequest={(options) => uploadTaxonomyImage("positive", options)}
												beforeUpload={(file) =>
												{
													if (!ALLOWED_UPLOAD_TYPES.includes(file.type))
													{
														message.error("Formato immagine non supportato (usa PNG/JPG/WEBP)");
														return Upload.LIST_IGNORE;
													}

													if (file.size > MAX_UPLOAD_SIZE)
													{
														message.error("Immagine troppo grande (max 5MB)");
														return Upload.LIST_IGNORE;
													}

													return true;
												}}
												onRemove={() =>
												{
													modalForm.setFieldValue("imagePathPositive", null);
													modalForm.setFieldValue("imagePath", null);
													return true;
												}}
											>
												{hasPositiveImage ? null : (
													<div className="org-taxonomy-upload">
														<PictureOutlined/>
														<div>Positiva</div>
													</div>
												)}
											</Upload>
										</Form.Item>
									</Card>


									<Card title="Immagine negativa" style={{ textAlign: 'center'}}>
										<Form.Item
											name="imageFileNegative"
											valuePropName="fileList"
											getValueFromEvent={(event) =>
											{
												const fileList = Array.isArray(event) ? event : (event?.fileList || []);
												const list = fileList.slice(-1);
												return list.map(item => ({
													uid: item.uid,
													name: item.name,
													status: item.status,
													url: item.url || (item.response?.imagePath ? normalizeImagePath(item.response.imagePath) : undefined),
													thumbUrl: item.thumbUrl,
													type: item.type,
													size: item.size,
													percent: item.percent,
													response: item.response?.imagePath ? {imagePath: item.response.imagePath} : undefined
												}));
											}}
											extra="Immagine con fondo scuro"
										>
											<Upload
												accept="image/*"
												listType="picture-card"
												maxCount={1}
												customRequest={(options) => uploadTaxonomyImage("negative", options)}
												beforeUpload={(file) =>
												{
													if (!ALLOWED_UPLOAD_TYPES.includes(file.type))
													{
														message.error("Formato immagine non supportato (usa PNG/JPG/WEBP)");
														return Upload.LIST_IGNORE;
													}

													if (file.size > MAX_UPLOAD_SIZE)
													{
														message.error("Immagine troppo grande (max 5MB)");
														return Upload.LIST_IGNORE;
													}

													return true;
												}}
												onRemove={() =>
												{
													modalForm.setFieldValue("imagePathNegative", null);
													return true;
												}}
											>
												{hasNegativeImage ? null : (
													<div className="org-taxonomy-upload">
														<PictureOutlined/>
														<div>Negativa</div>
													</div>
												)}
											</Upload>
										</Form.Item>
									</Card>
								</div>
							</>
						) : (
							<Form.Item
								label="Immagine"
								name="imageFile"
								valuePropName="fileList"
								getValueFromEvent={(event) =>
								{
									const fileList = Array.isArray(event) ? event : (event?.fileList || []);
									const list = fileList.slice(-1);
									return list.map(item => ({
										uid: item.uid,
										name: item.name,
										status: item.status,
										url: item.url || (item.response?.imagePath ? normalizeImagePath(item.response.imagePath) : undefined),
										thumbUrl: item.thumbUrl,
										type: item.type,
										size: item.size,
										percent: item.percent,
										response: item.response?.imagePath ? {imagePath: item.response.imagePath} : undefined
									}));
								}}
								extra="Carica un file immagine (PNG, JPG, WEBP) opzionale"
							>
								<Upload
									accept="image/*"
									listType="picture-card"
									maxCount={1}
									customRequest={(options) => uploadTaxonomyImage("single", options)}
									beforeUpload={(file) =>
									{
										if (!ALLOWED_UPLOAD_TYPES.includes(file.type))
										{
											message.error("Formato immagine non supportato (usa PNG/JPG/WEBP)");
											return Upload.LIST_IGNORE;
										}

										if (file.size > MAX_UPLOAD_SIZE)
										{
											message.error("Immagine troppo grande (max 5MB)");
											return Upload.LIST_IGNORE;
										}

										return true;
									}}
									onRemove={() =>
									{
										modalForm.setFieldValue("imagePath", null);
										return true;
									}}
								>
									{hasSingleImage ? null : (
										<div className="org-taxonomy-upload">
											<PictureOutlined/>
											<div>Carica</div>
										</div>
									)}
								</Upload>
							</Form.Item>
						)
					)}
				</Form>
			</Modal>
		</div>
	);
}
